import React, { ReactElement } from 'react'
import { Accordion, Icon, Paragraph } from '@scentregroup/ui'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import TradingHoursLine, { themeTypes } from '../trading-hours-line'
import { ParsedOpeningHour } from '../../helpers/parse-opening-hours/types'
import UtilityBoxItem from '../utility-box-item'
import style from './index.module.scss'

interface UtilityBoxOpeningHoursProps {
  openingHours: ParsedOpeningHour[]
  centreSlug?: string
  unlinkedHours?: boolean
  wrapperNoBorder?: boolean
  headerNoPadding?: boolean
  withinV2Design?: boolean
}

function calculateNumberOfHoursDisplayed(
  parsedHours: ParsedOpeningHour[]
): ParsedOpeningHour[] {
  const MAX_DISPLAYED_DAYS = 7
  const numberOfDaysDisplayed = Math.min(MAX_DISPLAYED_DAYS, parsedHours.length)
  return parsedHours.slice(0, numberOfDaysDisplayed)
}

const UtilityBoxOpeningHours: React.FC<UtilityBoxOpeningHoursProps> = ({
  openingHours,
  centreSlug,
  unlinkedHours,
  wrapperNoBorder,
  headerNoPadding,
  withinV2Design,
}): ReactElement | null => {
  if (openingHours.length === 0) {
    return null
  }

  const displayedHours = calculateNumberOfHoursDisplayed(openingHours)
  const headerHours = displayedHours[0]
  const contentHours = displayedHours.slice(1)

  return (
    <>
      <Accordion
        name="Opening Hours"
        id="opening-hours"
        type="small"
        classNames={{
          wrapper: classNames(style.openingHoursWrapper, {
            [style.wrapperNoBorder]: wrapperNoBorder,
          }),
          header: classNames(style.openingHoursTitle, {
            [style.headerNoPadding]: headerNoPadding,
          }),

          content: style.openingHoursContent,
        }}
        headerElement={
          <div className={style.headerElement}>
            <Icon
              type="hours"
              className={style.image}
              role="heading"
              aria-label="Opening hours"
              aria-level="4"
            />
            <TradingHoursLine
              openHourDay={headerHours}
              theme={themeTypes.leftAligned}
            />
          </div>
        }
      >
        <div className={withinV2Design ? style.tradingHoursLinesContainer : ''}>
          {contentHours.map(day => (
            <TradingHoursLine
              openHourDay={day}
              theme={themeTypes.leftAligned}
              key={day.day}
            />
          ))}
        </div>
      </Accordion>
      {unlinkedHours && (
        <UtilityBoxItem
          icon="hours"
          role="heading"
          aria-label="Hours may differ"
        >
          <Paragraph type="medium">
            Hours may differ from Centre hours
          </Paragraph>
          <Paragraph>
            <Link
              to={`/${centreSlug}/opening-hours`}
              className={style.differentHoursLink}
            >
              View Centre Hours
            </Link>
          </Paragraph>
        </UtilityBoxItem>
      )}
    </>
  )
}

UtilityBoxOpeningHours.defaultProps = {
  openingHours: [],
}

export default UtilityBoxOpeningHours
